import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { FiSearch, FiPlus, FiTrash2, FiEdit2, FiEye, FiAlertTriangle, FiChevronLeft, FiChevronRight, FiInbox } from 'react-icons/fi'
import '../../styles/AdminTable.css'

const ITEMS_PER_PAGE = 10
const PARENT = 'Bureau'
const DIM_ROWS = [
  { size: '1', dimKey: 'dimension1', priceKey: 'prix1', required: true },
  { size: '2', dimKey: 'dimension2', priceKey: 'prix2', required: false },
  { size: '3', dimKey: 'dimension3', priceKey: 'prix3', required: false },
]

const API = process.env.REACT_APP_API_URL

const TableBureau = () => {
  const authorization = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${authorization}` }
  const navigate = useNavigate()

  const [infob, setInfob] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categFilter, setCategFilter] = useState('all')
  const [sortBy, setSortBy] = useState('recent')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [categories, setCategories] = useState([])

  useEffect(() => {
    axios.get(`${API}/sous-categorie/lister`, { headers: { parent: PARENT } })
      .then((res) => setCategories(res.data))
      .catch(() => {})
  }, [])

  const editFileInputRef = useRef(null)
  const [editTarget, setEditTarget] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [editNewImage, setEditNewImage] = useState(null)
  const [editNewPreview, setEditNewPreview] = useState(null)
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [editError, setEditError] = useState(null)

  const handleAuthError = (err) => {
    if ([401, 403, 404].includes(err.response?.status)) {
      localStorage.clear()
      navigate('/login')
    }
  }

  const fetchData = () => {
    setLoading(true)
    axios.get(`${API}/bureau/lister`)
      .then((res) => setInfob(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [])

  const filtered = useMemo(() => {
    let arr = [...infob]
    if (search.trim()) {
      const s = search.toLowerCase()
      arr = arr.filter((t) => t.titre?.toLowerCase().includes(s) || t.reference?.toLowerCase().includes(s))
    }
    if (categFilter !== 'all') arr = arr.filter((t) => t.type === categFilter)
    if (sortBy === 'prixAsc') arr.sort((a, b) => a.prix - b.prix)
    else if (sortBy === 'prixDesc') arr.sort((a, b) => b.prix - a.prix)
    else if (sortBy === 'nomA') arr.sort((a, b) => a.titre?.localeCompare(b.titre))
    else if (sortBy === 'nomZ') arr.sort((a, b) => b.titre?.localeCompare(a.titre))
    else arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    return arr
  }, [infob, search, categFilter, sortBy])

  const nbPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE))
  const pageData = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  useEffect(() => { setPage(1) }, [search, categFilter, sortBy])

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const toggleSelectAll = () => setSelected(selected.length === pageData.length ? [] : pageData.map((t) => t._id))

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await axios.get(`${API}/bureau/${deleteTarget._id}/delete`, { headers })
      setInfob((t) => t.filter((x) => x._id !== deleteTarget._id))
      toast.success('Bureau supprimé')
      setDeleteTarget(null)
    } catch (err) {
      toast.error('Échec de la suppression')
      handleAuthError(err)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!selected.length) return
    if (!window.confirm(`Supprimer ${selected.length} bureau(x) sélectionné(s) ?`)) return
    try {
      await Promise.all(selected.map((id) => axios.get(`${API}/bureau/${id}/delete`, { headers })))
      setInfob((t) => t.filter((x) => !selected.includes(x._id)))
      setSelected([])
      toast.success('Bureaux supprimés')
    } catch (err) {
      toast.error('Échec de la suppression groupée')
      handleAuthError(err)
    }
  }

  const openEdit = (t) => {
    setEditTarget(t)
    setEditForm({
      titre: t.titre || '',
      reference: t.reference || '',
      type: t.type || '',
      disponibilite: t.disponibilite || '',
      description: t.description || '',
      description2: t.description2 || '',
      dimension1: t.dimension1 || '',
      dimension2: t.dimension2 || '',
      dimension3: t.dimension3 || '',
      prix: t.prix ?? '',
      prix1: t.prix1 ?? '',
      prix2: t.prix2 ?? '',
      prix3: t.prix3 ?? '',
    })
    setEditNewImage(null)
    setEditNewPreview(null)
    setEditError(null)
  }

  const closeEdit = () => {
    if (editNewPreview) URL.revokeObjectURL(editNewPreview)
    setEditTarget(null)
    setEditForm(null)
    setEditNewImage(null)
    setEditNewPreview(null)
    setEditError(null)
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEditForm((f) => ({ ...f, [name]: value }))
  }

  const handleEditFile = (fileList) => {
    const file = fileList?.[0]
    if (!file) return
    if (editNewPreview) URL.revokeObjectURL(editNewPreview)
    setEditNewImage(file)
    setEditNewPreview(URL.createObjectURL(file))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editTarget) return
    setEditSubmitting(true)
    setEditError(null)
    const formData = new FormData()
    Object.entries(editForm).forEach(([key, val]) => formData.append(key, val))
    if (editNewImage) formData.append('image', editNewImage)

    try {
      await axios.post(`${API}/bureau/${editTarget._id}/modifier`, formData, { headers })
      setInfob((ts) => ts.map((t) => (t._id === editTarget._id ? { ...t, ...editForm } : t)))
      toast.success('Bureau modifié')
      closeEdit()
    } catch (err) {
      setEditError('Une erreur est survenue lors de la modification.')
      handleAuthError(err)
    } finally {
      setEditSubmitting(false)
    }
  }

  return (
    <div className="atl-page">
      <Toaster />

      <div className="atl-header">
        <div>
          <h1 className="atl-title">Bureaux</h1>
          <p className="atl-subtitle">{loading ? 'Chargement…' : `${filtered.length} bureau${filtered.length > 1 ? 'x' : ''}`}</p>
        </div>
        <Link to="/admin/Bureaux/ajouter" className="atl-btn-add">
          <FiPlus size={16} />
          Ajouter un bureau
        </Link>
      </div>

      <div className="atl-toolbar">
        <div className="atl-search">
          <FiSearch size={15} />
          <input
            type="text"
            placeholder="Rechercher un titre ou une référence…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="atl-search__clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        <select className="atl-select" value={categFilter} onChange={(e) => setCategFilter(e.target.value)}>
          <option value="all">Toutes les catégories</option>
          {categories.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
        </select>

        <select className="atl-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="recent">Plus récent</option>
          <option value="prixAsc">Prix croissant</option>
          <option value="prixDesc">Prix décroissant</option>
          <option value="nomA">Nom A → Z</option>
          <option value="nomZ">Nom Z → A</option>
        </select>

        {selected.length > 0 && (
          <button className="atl-bulk-delete" onClick={handleBulkDelete}>
            <FiTrash2 size={14} />
            Supprimer ({selected.length})
          </button>
        )}
      </div>

      <div className="atl-table-wrap">
        <table className="atl-table">
          <thead>
            <tr>
              <th className="atl-th-check">
                <input type="checkbox" checked={pageData.length > 0 && selected.length === pageData.length} onChange={toggleSelectAll} />
              </th>
              <th>Photo</th>
              <th>Titre</th>
              <th>Référence</th>
              <th>Catégorie</th>
              <th>Dimensions</th>
              <th>Disponibilité</th>
              <th>Prix</th>
              <th className="atl-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="atl-skeleton-row"><td colSpan={9}><div className="atl-skeleton" /></td></tr>
              ))
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={9} className="atl-empty">
                  <FiInbox size={36} />
                  <p>Aucun bureau trouvé</p>
                </td>
              </tr>
            ) : (
              pageData.map((t) => (
                <tr key={t._id} className={selected.includes(t._id) ? 'atl-row--selected' : ''}>
                  <td className="atl-th-check">
                    <input type="checkbox" checked={selected.includes(t._id)} onChange={() => toggleSelect(t._id)} />
                  </td>
                  <td><div className="atl-thumb"><img src={t.image} alt={t.titre} /></div></td>
                  <td><span className="atl-title-cell">{t.titre}</span></td>
                  <td><span className="atl-ref">{t.reference}</span></td>
                  <td><span className="atl-tag">{t.type}</span></td>
                  <td><span className="atl-dims">{[t.dimension1, t.dimension2, t.dimension3].filter(Boolean).join(' · ')}</span></td>
                  <td><span className="atl-dims">{t.disponibilite}</span></td>
                  <td><span className="atl-price">{t.prix} DT</span></td>
                  <td className="atl-th-actions">
                    <div className="atl-actions">
                      <button className="atl-action-btn" title="Voir le produit" onClick={() => window.open(`/bureau/details/${t._id}`, '_blank')}>
                        <FiEye size={14} />
                      </button>
                      <button className="atl-action-btn atl-action-btn--edit" title="Modifier" onClick={() => openEdit(t)}>
                        <FiEdit2 size={14} />
                      </button>
                      <button className="atl-action-btn atl-action-btn--delete" title="Supprimer" onClick={() => setDeleteTarget(t)}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && nbPages > 1 && (
        <div className="atl-pagination">
          <button className="atl-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}><FiChevronLeft /></button>
          {Array.from({ length: nbPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`atl-page-btn ${p === page ? 'atl-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="atl-page-btn" disabled={page === nbPages} onClick={() => setPage((p) => p + 1)}><FiChevronRight /></button>
        </div>
      )}

      {deleteTarget && (
        <div className="atl-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="atl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="atl-modal__icon"><FiAlertTriangle size={22} /></div>
            <h3 className="atl-modal__title">Supprimer ce bureau ?</h3>
            <p className="atl-modal__text"><strong>{deleteTarget.titre}</strong> sera supprimé définitivement. Cette action est irréversible.</p>
            <div className="atl-modal__actions">
              <button className="atl-modal__btn atl-modal__btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
              <button className="atl-modal__btn atl-modal__btn--confirm" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}

      {editTarget && editForm && (
        <div className="atl-modal-overlay" onClick={() => !editSubmitting && closeEdit()}>
          <div className="atl-modal atl-modal--edit" onClick={(e) => e.stopPropagation()}>
            <h3 className="atl-modal__title atl-modal__title--edit">Modifier le bureau</h3>

            <form onSubmit={handleEditSubmit}>
              <div className="atl-edit-grid">
                <div className="atl-edit-col">
                  <label className="atl-edit-label">Image</label>
                  <div className="atl-edit-photos-grid">
                    <div className="atl-edit-photo-item">
                      <img src={editNewPreview || editTarget.image} alt={editTarget.titre} className="atl-edit-photo-thumb" />
                    </div>
                  </div>
                  <button type="button" className="atl-edit-replace-btn" onClick={() => editFileInputRef.current.click()}>
                    Remplacer l'image
                  </button>
                  <input ref={editFileInputRef} type="file" accept="image/*" hidden onChange={(e) => handleEditFile(e.target.files)} />

                  <label className="atl-edit-label">Description</label>
                  <textarea className="atl-edit-input" rows={4} value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />

                  <label className="atl-edit-label">Description détaillée</label>
                  <textarea className="atl-edit-input" rows={6} value={editForm.description2} onChange={(e) => setEditForm((f) => ({ ...f, description2: e.target.value }))} />
                </div>

                <div className="atl-edit-col">
                  <label className="atl-edit-label">Titre</label>
                  <input className="atl-edit-input" type="text" name="titre" value={editForm.titre} onChange={handleEditChange} />

                  <label className="atl-edit-label">Référence</label>
                  <input className="atl-edit-input" type="text" name="reference" value={editForm.reference} onChange={handleEditChange} />

                  <label className="atl-edit-label">Catégorie</label>
                  <select className="atl-edit-input" name="type" value={editForm.type} onChange={handleEditChange}>
                    {categories.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
                  </select>

                  <label className="atl-edit-label">Disponibilité</label>
                  <select className="atl-edit-input" name="disponibilite" value={editForm.disponibilite} onChange={handleEditChange}>
                    <option value="En stock">En stock</option>
                    <option value="Hors stock">Hors stock</option>
                    <option value="Sur commande">Sur commande</option>
                    <option value="En arrivage">En arrivage</option>
                  </select>

                  <label className="atl-edit-label">Prix de base</label>
                  <input className="atl-edit-input" type="number" name="prix" value={editForm.prix} onChange={handleEditChange} />

                  <label className="atl-edit-label">Dimensions &amp; Tarifs</label>
                  {DIM_ROWS.map(({ size, dimKey, priceKey, required }) => (
                    <div key={size} className="atl-edit-dim-row">
                      <span className="atl-edit-dim-badge">{size}</span>
                      <input className="atl-edit-input atl-edit-input--sm" type="text" name={dimKey} value={editForm[dimKey]} onChange={handleEditChange} placeholder={required ? 'Dimension' : 'Optionnel'} />
                      <input className="atl-edit-input atl-edit-input--sm" type="number" name={priceKey} value={editForm[priceKey]} onChange={handleEditChange} placeholder="Prix" />
                    </div>
                  ))}
                </div>
              </div>

              {editError && <p className="atl-error">{editError}</p>}

              <div className="atl-modal__actions">
                <button type="button" className="atl-modal__btn atl-modal__btn--cancel" onClick={closeEdit} disabled={editSubmitting}>Annuler</button>
                <button type="submit" className="atl-modal__btn atl-modal__btn--save" disabled={editSubmitting}>{editSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableBureau
